/** Iter 106 — send a booking to the customer via Email or WhatsApp.
 * Fetches an editable pre-filled message from /send-preview. */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Mail, MessageCircle, Loader2 } from "lucide-react";

export default function SendBookingDialog({ bookingId, open, onClose }) {
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !bookingId) return;
    setPreview(null);
    api.get(`/bookings/${bookingId}/send-preview`).then((r) => {
      setPreview(r.data);
      setSubject(r.data.email.subject);
      setBody(r.data.email.body);
      setToEmail(r.data.email.to || "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, [open, bookingId]);

  const sendEmail = async () => {
    if (!toEmail.trim()) { toast.error("Customer email is missing"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/bookings/${bookingId}/send-email`, {
        subject, body, attach_pdf: attachPdf, to_email: toEmail.trim(),
      });
      toast.success(`Booking emailed to ${data.sent_to}${data.attached_pdf ? " (PDF attached)" : ""}`);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  const sendWhatsApp = () => {
    const phone = (preview?.whatsapp?.to || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
    if (!phone) { toast.error("Customer phone number is missing"); return; }
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(body)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="send-booking-dialog">
        <DialogHeader>
          <DialogTitle>Send booking to customer</DialogTitle>
          <DialogDescription>Review or edit the message, then send by email or WhatsApp.</DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Customer email</Label>
              <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} className="mt-1" data-testid="send-booking-to" />
            </div>
            <div>
              <Label>Subject (email)</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="send-booking-subject" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} className="mt-1 text-xs font-mono" data-testid="send-booking-body" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} className="h-4 w-4 accent-blue-600" data-testid="send-booking-attach-pdf" />
              Attach booking confirmation PDF (email only)
            </label>
            <div className="flex gap-2 pt-1">
              <Button onClick={sendEmail} disabled={sending} className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700" data-testid="send-booking-email-btn">
                {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Mail className="h-4 w-4 mr-1.5" />} Email booking
              </Button>
              <Button onClick={sendWhatsApp} variant="outline" className="flex-1 rounded-full border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="send-booking-whatsapp-btn">
                <MessageCircle className="h-4 w-4 mr-1.5" /> Send via WhatsApp
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
